import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminListHostBankAccounts, adminUpdateHostBankAccountStatus } from '../../api/adminHosts';
import { useToast } from '../../components/ToastContext';

const statusOptions = [
  { value: '', label: 'All statuses' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'VERIFIED', label: 'Verified' },
  { value: 'REJECTED', label: 'Rejected' },
];

const AdminHostBankAccounts = ({ token }) => {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('PENDING');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, total: 0, limit: 50 });

  const load = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminListHostBankAccounts(token, {
        status,
        q: query,
        page,
        limit: pagination.limit,
      });
      setItems(res.items || []);
      setPagination(res.pagination || { page, totalPages: 1, total: res.items?.length || 0, limit: pagination.limit });
    } catch (err) {
      setError(err.message || 'Failed to load bank accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (hostId) => {
    try {
      await adminUpdateHostBankAccountStatus(token, hostId, 'VERIFIED');
      toast.success('Bank account verified');
      load(pagination.page || 1);
    } catch (err) {
      toast.error(err.message || 'Approval failed');
    }
  };

  const reject = async (hostId) => {
    const reason = window.prompt('Rejection reason');
    if (!reason || !reason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    try {
      await adminUpdateHostBankAccountStatus(token, hostId, 'REJECTED', reason.trim());
      toast.success('Bank account rejected');
      load(pagination.page || 1);
    } catch (err) {
      toast.error(err.message || 'Reject failed');
    }
  };

  return (
    <div className="admin-card rounded-lg bg-white p-4 shadow-sm ring-1 ring-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 pb-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-emerald-700">Hosts / Partners</p>
          <h1 className="admin-page-title text-xl font-bold text-slate-900">Bank Account Verification</h1>
          <p className="text-sm text-slate-600">Review and approve host payout bank accounts.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input
            type="search"
            placeholder="Search host, bank, IFSC"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load(1)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          />
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
          >
            {statusOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => load(1)}
            className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800"
          >
            Apply
          </button>
        </div>
      </div>

      {error && <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div>}

      <div className="mt-4 overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">Host</th>
              <th className="px-4 py-3">Contact</th>
              <th className="px-4 py-3">Bank</th>
              <th className="px-4 py-3">IFSC</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  Loading...
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-3 text-slate-500">
                  No bank accounts found.
                </td>
              </tr>
            )}
            {items.map((row) => (
              <tr key={row.id} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-semibold text-slate-900">{row.host?.full_name || 'Host'}</td>
                <td className="px-4 py-3 text-slate-700">
                  {row.host?.phone || '--'}
                  <div className="text-xs text-slate-500">{row.host?.email || ''}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">
                  {row.bank_name}
                  <div className="text-xs text-slate-500">{row.account_number_masked}</div>
                </td>
                <td className="px-4 py-3 text-slate-700">{row.ifsc}</td>
                <td className="px-4 py-3 text-xs">
                  <span className="rounded-full bg-slate-100 px-2 py-1 font-semibold text-slate-700">
                    {(row.status || 'pending').toUpperCase()}
                  </span>
                </td>
                <td className="px-4 py-3 flex gap-2">
                  <Link
                    to={`/admin/hosts/${row.host_id}`}
                    className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    View
                  </Link>
                  <button
                    type="button"
                    onClick={() => approve(row.host_id)}
                    className="rounded-lg border border-emerald-200 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    onClick={() => reject(row.host_id)}
                    className="rounded-lg border border-rose-200 px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-50"
                  >
                    Reject
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex items-center justify-between text-sm text-slate-600">
        <div>
          Page {pagination.page || 1} of {pagination.totalPages || 1}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => load(Math.max(1, (pagination.page || 1) - 1))}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) <= 1}
          >
            Prev
          </button>
          <button
            type="button"
            onClick={() => load((pagination.page || 1) + 1)}
            className="rounded-lg border border-slate-200 px-3 py-1 hover:bg-slate-50"
            disabled={(pagination.page || 1) >= (pagination.totalPages || 1)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminHostBankAccounts;
